import { ArrowDown } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import PortfolioCard from './portfolioCard';
import PortfolioDetail from './portfolioDetail';
import { motion, useInView } from 'framer-motion';
import { Portfolio } from '../../../types';
import {
  hasCaseStudy,
  portfolioCategories,
  portfolios,
} from '../../../data/portfolios';
import { slugify } from '../../../lib/utils';
import BottomSheet from '../../ui/bottom-sheet';

// Shareable query key: ?project=<slug> deep-links to an open case study, on
// both the home page and /portfolio.
const PROJECT_PARAM = 'project';

// Reading a case study can outlive the page — a mobile browser reclaiming the
// tab reloads it, and the open sheet is restored from the URL. Remembering the
// expanded grid for the tab keeps the list from collapsing back to four cards
// underneath it. Kept out of the URL deliberately: it isn't worth sharing.
const SHOW_ALL_KEY = 'portfolio:showAll';

// Private-mode browsers can throw on storage access; a collapsed grid is a far
// better failure than a blank page.
const readShowAll = () => {
  try {
    return sessionStorage.getItem(SHOW_ALL_KEY) === '1';
  } catch {
    return false;
  }
};

const writeShowAll = (value: boolean) => {
  try {
    if (value) sessionStorage.setItem(SHOW_ALL_KEY, '1');
    else sessionStorage.removeItem(SHOW_ALL_KEY);
  } catch {
    /* not persisting is fine */
  }
};

interface PortfolioSectionProps {
  title?: string;
  /** The home page cross-links to the AI work; the dedicated page doesn't. */
  showAiPortfolioLink?: boolean;
}

const PortfolioSection = ({
  title = 'UI/UX Portfolio',
  showAiPortfolioLink = false,
}: PortfolioSectionProps) => {
  const [tabSelected, setTabSelected] = useState('All');
  const [isMobile, setIsMobile] = useState(false);
  const [showAll, setShowAll] = useState(readShowAll);
  const [searchParams, setSearchParams] = useSearchParams();
  const [animationKey, setAnimationKey] = useState(0);

  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: '-100px' });

  // The URL is the source of truth for which sheet is open, so a shared link
  // (or the browser back/forward buttons) opens/closes the right project.
  // Only portfolios with a case study render a sheet, so the key is gated on it.
  const activeSlug = searchParams.get(PROJECT_PARAM);
  const selectedPortfolio =
    portfolios.find(
      (p) => hasCaseStudy(p) && slugify(p.title) === activeSlug,
    ) ?? null;

  useEffect(() => {
    writeShowAll(showAll);
  }, [showAll]);

  // Switching category collapses the grid. This lives on the click rather than
  // in an effect keyed on `tabSelected`, which would also fire on mount and
  // immediately wipe the remembered state.
  const handleTabClick = (category: string) => {
    if (category === tabSelected) return;
    setTabSelected(category);
    setShowAll(false);
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 600);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Trigger re-animation when tab changes
    setAnimationKey((prev) => prev + 1);
  }, [tabSelected]);

  const handlePortfolioClick = (portfolio: Portfolio) => {
    // Only portfolios with a case study open a sheet; keep others as no-ops.
    if (!hasCaseStudy(portfolio)) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(PROJECT_PARAM, slugify(portfolio.title));
        return next;
      },
      { replace: false },
    );
  };

  const handleCloseSheet = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(PROJECT_PARAM);
        return next;
      },
      { replace: false },
    );
  };

  // Filter portfolios by selected tab
  const filteredPortfolios = portfolios.filter((portfolio) =>
    portfolio.categories.includes(tabSelected),
  );

  // Sort so first 'Light', then 'Dark'
  const sortedPortfolios = filteredPortfolios.sort((a, b) => {
    const aIsLight = a.categories.includes('Light') ? 0 : 1;
    const bIsLight = b.categories.includes('Light') ? 0 : 1;
    return aIsLight - bIsLight;
  });

  // Show only first 4 items if showAll is false
  const displayedPortfolios = showAll
    ? sortedPortfolios
    : sortedPortfolios.slice(0, 4);

  return (
    <div className="overflow-x-hidden w-full mt-4 md:mt-8" id="portfolio">
      {/* The category tabs already fill the header row, so the AI cross-link
          sits on its own line above it. */}
      <div
        className={`flex flex-col sm:flex-row py-4 justify-between gap-4 sm:gap-0`}
      >
        <div className="flex flex-col gap-2">
          <div className="text-sm text-brand font-medium">Showcasing</div>
          <div className="text-xl sm:text-2xl font-medium">{title}</div>
        </div>
        <div className="flex gap-4 md:gap-6 mb-1 pt-2 md:pt-8 items-end overflow-x-auto pb-2">
          {portfolioCategories.map((category, index) => (
            <div
              key={index}
              className={`relative text-sm h-fit font-medium text-blackSecondary hover:text-brand hover:opacity-100 brand-transition cursor-pointer flex-shrink-0 ${
                tabSelected === category
                  ? 'text-brand opacity-100'
                  : 'opacity-50'
              }`}
              onClick={() => handleTabClick(category)}
            >
              {category}
              {tabSelected === category && (
                <div className="absolute -bottom-1 left-0 h-[1.5px] w-full bg-brand rounded-full"></div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="relative" ref={ref}>
        <motion.div
          key={animationKey}
          className="flex flex-col gap-3 sm:gap-6 md:gap-8 pt-2 md:pt-4 overflow-y-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: isInView ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        >
          {displayedPortfolios.map((portfolio, index) => (
            <PortfolioCard
              key={`${portfolio.title}-${index}`}
              portfolio={portfolio}
              isMobile={isMobile}
              onClick={() => handlePortfolioClick(portfolio)}
              index={index}
              isInView={isInView}
            />
          ))}
        </motion.div>

        {/* Show More Gradient Overlay and Button */}
        {!showAll && sortedPortfolios.length > 4 && (
          <div className="relative">
            <div className="absolute inset-x-0 -top-16 sm:-top-24 md:-top-32 h-16 sm:h-24 md:h-32 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
            <div className="flex justify-center pt-6 sm:pt-8">
              <button
                onClick={() => setShowAll(true)}
                className="flex gap-2 items-center justify-center text-gray-500 font-medium text-sm border border-gray-300 rounded-full px-4 sm:px-6 py-2 hover:bg-gray-100 transition-colors duration-300"
              >
                <p>Show More</p>
                <ArrowDown className="h-4 w-4 text-gray-600" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Sheet */}
      <BottomSheet isOpen={!!selectedPortfolio} onClose={handleCloseSheet}>
        {selectedPortfolio && <PortfolioDetail portfolio={selectedPortfolio} />}
      </BottomSheet>
    </div>
  );
};

export default PortfolioSection;
